(() => {
  'use strict';

  const HEADER_SELECTOR = '[data-mf-header-v84]';
  const ACTIVE_CLASS = 'is-open';
  const SCROLLED_CLASS = 'is-scrolled';
  const SUBMENU_CLASS = 'is-submenu-open';
  const BODY_LOCK_CLASS = 'mf-header-v84-lock';
  const MOBILE_QUERY = '(max-width: 989px)';

  let cartRefreshTimer = null;
  let fetchHooked = false;
  let xhrHooked = false;

  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

  function setMobileTop(header) {
    if (!header) return;

    const rect = header.getBoundingClientRect();
    const top = Math.max(0, rect.bottom);

    header.style.setProperty('--mf-header-mobile-top', `${Math.round(top)}px`);
  }

  function updateScrolledState(header) {
    if (!header) return;

    if (window.scrollY > 8) {
      header.classList.add(SCROLLED_CLASS);
    } else {
      header.classList.remove(SCROLLED_CLASS);
    }
  }

  function closeSubmenus(header) {
    const items = header.querySelectorAll(`.${SUBMENU_CLASS}`);

    items.forEach((item) => {
      item.classList.remove(SUBMENU_CLASS);

      const toggle = item.querySelector('.js-mf-header-submenu-toggle');

      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  function openMenu(header, burger) {
    header.classList.add(ACTIVE_CLASS);
    burger.setAttribute('aria-expanded', 'true');
    document.body.classList.add(BODY_LOCK_CLASS);
    setMobileTop(header);
  }

  function closeMenu(header, burger) {
    header.classList.remove(ACTIVE_CLASS);
    burger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove(BODY_LOCK_CLASS);
    closeSubmenus(header);
  }

  function toggleMobileSubmenu(header, toggle) {
    const item = toggle.closest('.mf-header-combo-v84__nav-item');

    if (!item) return;

    const isOpen = item.classList.contains(SUBMENU_CLASS);

    closeSubmenus(header);

    if (!isOpen) {
      item.classList.add(SUBMENU_CLASS);
      toggle.setAttribute('aria-expanded', 'true');
    }
  }

  function updateCartBadges(count) {
    const safeCount = Number.isFinite(Number(count)) ? Number(count) : 0;
    const badges = document.querySelectorAll('[data-mf-cart-count]');

    badges.forEach((badge) => {
      badge.textContent = String(safeCount);
      badge.setAttribute('aria-label', `${safeCount} artículos en carrito`);
    });
  }

  async function refreshCartCount() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) return;

      const cart = await response.json();

      if (cart && typeof cart.item_count !== 'undefined') {
        updateCartBadges(cart.item_count);
      }
    } catch (error) {
      /*
        Silencioso por estabilidad:
        Si Starlite o una app bloquea /cart.js temporalmente,
        no rompemos navegación ni menú.
      */
    }
  }

  function scheduleCartRefresh(delay = 180) {
    window.clearTimeout(cartRefreshTimer);

    cartRefreshTimer = window.setTimeout(() => {
      refreshCartCount();
    }, delay);
  }

  function isCartRequest(url) {
    if (!url) return false;

    const target = String(url);

    return (
      target.includes('/cart/add') ||
      target.includes('/cart/change') ||
      target.includes('/cart/update') ||
      target.includes('/cart/clear')
    );
  }

  function hookFetchForCartUpdates() {
    if (fetchHooked || typeof window.fetch !== 'function') return;

    fetchHooked = true;

    const originalFetch = window.fetch;

    window.fetch = async function patchedFetch(...args) {
      const requestUrl =
        typeof args[0] === 'string'
          ? args[0]
          : args[0] && args[0].url
            ? args[0].url
            : '';

      const response = await originalFetch.apply(this, args);

      if (isCartRequest(requestUrl)) {
        scheduleCartRefresh(220);
      }

      return response;
    };
  }

  function hookXHRForCartUpdates() {
    if (xhrHooked || typeof window.XMLHttpRequest !== 'function') return;

    xhrHooked = true;

    const originalOpen = window.XMLHttpRequest.prototype.open;
    const originalSend = window.XMLHttpRequest.prototype.send;

    window.XMLHttpRequest.prototype.open = function patchedOpen(method, url, ...rest) {
      this.__mfHeaderCartUrl = url;
      return originalOpen.call(this, method, url, ...rest);
    };

    window.XMLHttpRequest.prototype.send = function patchedSend(...args) {
      if (isCartRequest(this.__mfHeaderCartUrl)) {
        this.addEventListener('loadend', () => {
          scheduleCartRefresh(220);
        });
      }

      return originalSend.apply(this, args);
    };
  }

  function listenToThemeCartEvents() {
    const events = [
      'cart:updated',
      'cart:refresh',
      'cart:change',
      'cart:added',
      'ajaxProduct:added',
      'product:added',
      'cart-drawer:open',
      'cart:build'
    ];

    events.forEach((eventName) => {
      document.addEventListener(eventName, () => {
        scheduleCartRefresh(120);
      });
    });
  }

  function initHeader(header) {
    if (!header || header.dataset.mfHeaderReady === 'true') return;

    header.dataset.mfHeaderReady = 'true';

    const burger = header.querySelector('[data-mf-header-burger]');
    const nav = header.querySelector('[data-mf-header-nav]');
    const overlay = header.querySelector('[data-mf-header-overlay]');
    const submenuToggles = header.querySelectorAll('.js-mf-header-submenu-toggle');
    const closeLinks = header.querySelectorAll('.js-mf-header-close');

    if (!burger || !nav) return;

    updateScrolledState(header);
    setMobileTop(header);

    burger.addEventListener('click', () => {
      const expanded = burger.getAttribute('aria-expanded') === 'true';

      if (expanded) {
        closeMenu(header, burger);
      } else {
        openMenu(header, burger);
      }
    });

    if (overlay) {
      overlay.addEventListener('click', () => {
        closeMenu(header, burger);
      });
    }

    submenuToggles.forEach((toggle) => {
      toggle.addEventListener('click', (event) => {
        if (!isMobile()) return;

        event.preventDefault();
        toggleMobileSubmenu(header, toggle);
      });

      toggle.addEventListener('keydown', (event) => {
        if (!isMobile()) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        toggleMobileSubmenu(header, toggle);
      });

      toggle.addEventListener('mouseenter', () => {
        if (isMobile()) return;

        toggle.setAttribute('aria-expanded', 'true');
      });

      const parentItem = toggle.closest('.mf-header-combo-v84__nav-item');

      if (parentItem) {
        parentItem.addEventListener('mouseleave', () => {
          if (isMobile()) return;
          toggle.setAttribute('aria-expanded', 'false');
        });

        parentItem.addEventListener('focusout', (event) => {
          if (isMobile()) return;
          if (parentItem.contains(event.relatedTarget)) return;

          toggle.setAttribute('aria-expanded', 'false');
        });
      }
    });

    closeLinks.forEach((link) => {
      link.addEventListener('click', () => {
        closeMenu(header, burger);
      });
    });

    document.addEventListener('click', (event) => {
      if (!header.classList.contains(ACTIVE_CLASS)) return;
      if (header.contains(event.target)) return;

      closeMenu(header, burger);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!header.classList.contains(ACTIVE_CLASS)) return;

      closeMenu(header, burger);
      burger.focus({ preventScroll: true });
    });

    window.addEventListener(
      'resize',
      () => {
        setMobileTop(header);

        if (!isMobile()) {
          closeMenu(header, burger);
        }
      },
      { passive: true }
    );

    window.addEventListener(
      'scroll',
      () => {
        updateScrolledState(header);

        if (header.classList.contains(ACTIVE_CLASS)) {
          setMobileTop(header);
        }
      },
      { passive: true }
    );
  }

  function initAllHeaders() {
    document.querySelectorAll(HEADER_SELECTOR).forEach(initHeader);
  }

  function initCartSync() {
    hookFetchForCartUpdates();
    hookXHRForCartUpdates();
    listenToThemeCartEvents();
    refreshCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllHeaders();
      initCartSync();
    });
  } else {
    initAllHeaders();
    initCartSync();
  }

  document.addEventListener('shopify:section:load', () => {
    initAllHeaders();
    scheduleCartRefresh(120);
  });
})();